// KamSathi Admin Panel — Firebase Service
// All Firebase connections managed from this single file

import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';

// ── Admin Panel Firebase Config ──
const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId:     process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// ── Contractor Firebase Config ──
const contractorFirebaseConfig = {
  apiKey:            process.env.REACT_APP_CONTRACTOR_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_CONTRACTOR_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_CONTRACTOR_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_CONTRACTOR_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_CONTRACTOR_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_CONTRACTOR_APP_ID,
};

// ── HomeOwner Firebase Config ──
const homeownerFirebaseConfig = {
  apiKey:            process.env.REACT_APP_HOMEOWNER_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_HOMEOWNER_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_HOMEOWNER_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_HOMEOWNER_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_HOMEOWNER_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_HOMEOWNER_APP_ID,
};

// ── Dealer Firebase Config ──
const dealerFirebaseConfig = {
  apiKey:            process.env.REACT_APP_DEALER_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_DEALER_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_DEALER_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_DEALER_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_DEALER_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_DEALER_APP_ID,
};

// ── Initialize all 4 Firebase Apps ──
const app           = initializeApp(firebaseConfig);
const contractorApp = initializeApp(contractorFirebaseConfig, 'contractor');
const homeownerApp  = initializeApp(homeownerFirebaseConfig,  'homeowner');
const dealerApp     = initializeApp(dealerFirebaseConfig,     'dealer');

// ── Firestore instances ──
export const db           = getFirestore(app);            // Admin Panel DB
export const contractorDb = getFirestore(contractorApp);  // Contractor App DB
export const homeownerDb  = getFirestore(homeownerApp);   // HomeOwner App DB
export const dealerDb     = getFirestore(dealerApp);      // Dealer App DB

// ── Auth ──
export const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

// ── Sign in with Google ──
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return { user: result.user, error: null };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

// ── Sign out ──
export const signOutAdmin = async () => {
  try {
    await signOut(auth);
    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};

// ── Check if email is in admin whitelist ──
export const checkAdminAccess = async (email) => {
  try {
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const adminRef = collection(db, 'adminUsers');
    const q = query(adminRef, where('email', '==', email), where('isActive', '==', true));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return { allowed: false, adminData: null };
    const adminData = snapshot.docs[0].data();
    return { allowed: true, adminData };
  } catch (error) {
    console.error('Error checking admin access:', error);
    return { allowed: false, adminData: null };
  }
};

// ── Write audit log ──
export const writeAuditLog = async (action, details, adminEmail) => {
  try {
    const { collection, addDoc } = await import('firebase/firestore');
    await addDoc(collection(db, 'auditLogs'), {
      action,
      details,
      adminEmail,
      timestamp: serverTimestamp(),
    });
  } catch (error) {
    console.error('Audit log error:', error);
  }
};

// ── Auth state listener ──
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

export default app;